import { useCallback, useEffect, useState } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api } from '../api/apiClient';
import { useSelectedAccount } from '../App';
import DateRangeFilter from '../components/dateRangeFilters';

import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const RepairStatus = {
    Outstanding: 1,
    Completed: 2,
} as const;

type RepairStatus = typeof RepairStatus[keyof typeof RepairStatus];

interface RepairDto {
    workOrderId: number;
    type: number;
    status: RepairStatus;
    logged: string;
    appointment?: string | null;
    summary: string;
}

interface RepairUi {
    id: number;
    workOrderId: number;
    summary: string;
    status: RepairStatus;
    logged: string;
    appointment: string;
}

const MyRepairs = () => {
    const { selectedAccount } = useSelectedAccount();

    const [repairs, setRepairs] = useState<RepairUi[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fromDate, setFromDate] = useState(selectedAccount?.tenancyStartDate || '1970-01-01');
    const [toDate, setToDate] = useState(selectedAccount?.tenancyEndDate || '2100-01-01');
    const [resetTrigger, setResetTrigger] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
    const [selectedRepairType, setSelectedRepairType] = useState<string>('1');

    const formatDateShort = (iso: string | null | undefined) => {
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const load = useCallback(() => {
        if (!selectedAccount?.accountReference) {
            setRepairs([]);
            return;
        }

        setLoading(true);
        setError(null);

        const controller = new AbortController();
        const signal = controller.signal;

        let isCurrent = true;

        const fetchData = async () => {
            try {
                const query = `/repairs?assetId=${encodeURIComponent(selectedAccount.assetId)}&repairType=${encodeURIComponent(selectedRepairType)}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
                const data = await api<RepairDto[]>(query, { signal });

                if (!isCurrent) {
                    return;
                }

                let filtered = data;
                if (selectedStatus !== 'ALL') {
                    filtered = data.filter(r => `${r.status}` === selectedStatus);
                }

                const ui: RepairUi[] = filtered.map((r, i) => ({
                    id: i,
                    workOrderId: r.workOrderId,
                    summary: r.summary,
                    status: r.status,
                    logged: formatDateShort(r.logged),
                    appointment: formatDateShort(r.appointment) || 'Not Scheduled'
                })).sort((a, b) => b.workOrderId - a.workOrderId);

                setRepairs(ui);
            } catch (e: any) {
                if (e.name === 'AbortError') {
                    console.log('Request canceled for a newer one, keep loading true');
                } else {
                    setError(e.message || 'Failed to load repairs');
                    console.error(error);
                    setRepairs([]);
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            controller.abort();
        };
    }, [fromDate, toDate, selectedStatus, selectedRepairType, selectedAccount]);

    useEffect(() => {
        const abortLoad = load();
        return () => abortLoad?.();
    }, [load]);

    const handleFilterChange = (from: string, to: string) => {
        setFromDate(from || selectedAccount?.tenancyStartDate || '1970-01-01');
        setToDate(to || selectedAccount?.tenancyEndDate || '2100-01-01');
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedStatus(e.target.value);
    };

    const handleRepairTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedRepairType(e.target.value);
    };

    const handleFilterReset = () => {
        setResetTrigger(prev => !prev);
        setFromDate(selectedAccount?.tenancyStartDate || '1970-01-01');
        setToDate(selectedAccount?.tenancyEndDate || '2100-01-01');
        setSelectedStatus('ALL');
        setSelectedRepairType(RepairStatus.Outstanding.toString());
        load();
    };

    const columns: GridColDef[] = [
        {
            field: 'statusIcon',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const status = params.row?.status;

                if (status === RepairStatus.Completed) {
                    return <CheckCircleIcon className="status-icon--completed" />;
                }

                return <ErrorOutlineIcon className="status-icon--outstanding" />;
            }
        },
        { field: 'workOrderId', headerName: 'Request Id', width: 120 },
        {
            field: 'logged',
            headerName: 'Logged',
            width: 130,
            sortComparator: (v1: string, v2: string) => {
                const d1 = new Date(v1);
                const d2 = new Date(v2);

                const t1 = isNaN(d1.getTime()) ? 0 : d1.getTime();
                const t2 = isNaN(d2.getTime()) ? 0 : d2.getTime();

                return t1 - t2;
            },
        },
        { 
            field: 'status', 
            headerName: 'Status', 
            width: 120,
            renderCell: (params) => params.value === 2 ? "Completed" : "Outstanding"
        },
        { field: 'appointment', headerName: 'Appointment', width: 160 },
        { field: 'summary', headerName: 'Summary', flex: 1, minWidth: 100 }
    ];


    return (
        <div className="page myrepairs">
            <div className="myrepairs__filters">
                <DateRangeFilter
                    fromDate={selectedAccount?.tenancyStartDate}
                    toDate={selectedAccount?.tenancyEndDate}
                    minDate={selectedAccount?.tenancyStartDate}
                    maxDate={selectedAccount?.tenancyEndDate}
                    onFilterChange={handleFilterChange}
                    cssClasses="date-range-filter--abs"
                    resetTrigger={resetTrigger}
                />

                <div className="input input--select">
                    <label className="input__label" htmlFor="repairtype-select">Repair Type</label>
                    <select
                        id="repairtype-select"
                        className="input__inp"
                        value={selectedRepairType}
                        onChange={handleRepairTypeChange}
                    >
                        <option value="1">My Repairs</option>
                        <option value="2">Communal</option>
                    </select>
                </div>

                <div className="input input--select">
                    <label className="input__label" htmlFor="status-select">Status</label>
                    <select id="status-select" className="input__inp" value={selectedStatus} onChange={handleStatusChange}>
                        <option value="ALL">All</option>
                        <option value="1">Outstanding</option>
                        <option value="2">Completed</option>
                    </select>
                </div>

                <button className="btn btn--secondary" onClick={handleFilterReset}>
                    Reset
                </button>
            </div>

            <div className="myrepairs__grid">
                <DataGrid
                    rows={repairs}
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    loading={loading}
                    pageSizeOptions={[12]}
                    initialState={{ pagination: { paginationModel: { pageSize: 12 } } }}
                    sx={{
                        '& .MuiDataGrid-columnSeparator': { display: 'none' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                            display: 'flex !important',
                            alignItems: 'center',
                            whiteSpace: 'normal !important',
                            overflow: 'visible !important',
                            lineHeight: 1.4,
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        },

                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            overflow: 'visible',
                        },
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'circular-progress',
                            noRowsVariant: 'circular-progress',
                        },
                    }}
                />
            </div>

            {/*{error && <p className="myrepairs__error">Error: {error}</p>}*/}
        </div>
    );
};

export default MyRepairs;
